import React, { createContext, ReactNode, useCallback, useContext, useState } from 'react';

import { useModal } from '@futrli/shared-services';
import { ProcessedUserRow } from '@futrli/views/processors/users/usersList';

import { DeleteUserDialog } from '../../dialogs/delete-user-dialog/delete-user-dialog';
import { ModifyUserSidebar } from '../../sidebars/modify-user-sidebar/modify-user-sidebar';

interface UsersActionsContextValue {
  handleDeleteUserClick: (user: ProcessedUserRow) => void;
  handleEditUserClick: (user: ProcessedUserRow) => void;
}

interface UsersActionsProviderProps {
  children: ReactNode;
  workspaceId: string;
}

const UsersActionsContext = createContext<UsersActionsContextValue>(({} as unknown) as UsersActionsContextValue);

export const UsersActionsProvider = ({ children, workspaceId }: UsersActionsProviderProps) => {
  const deleteUserDialog = useModal();
  const modifyUserSidebar = useModal();
  const [userToDelete, setUserToDelete] = useState<ProcessedUserRow>();
  const [userToModify, setUserToModify] = useState<ProcessedUserRow>();

  const handleDeleteUserClick = useCallback(
    (user: ProcessedUserRow) => {
      setUserToDelete(user);
      deleteUserDialog.open();
    },
    [deleteUserDialog]
  );

  const handleEditUserClick = useCallback(
    (user: ProcessedUserRow) => {
      setUserToModify(user);
      modifyUserSidebar.open();
    },
    [modifyUserSidebar]
  );

  const value = {
    handleDeleteUserClick,
    handleEditUserClick
  };

  return (
    <UsersActionsContext.Provider value={value}>
      {userToDelete && deleteUserDialog.isOpen && (
        <DeleteUserDialog
          isOpen={deleteUserDialog.isOpen}
          onClose={deleteUserDialog.close}
          userId={userToDelete.id}
          workspaceId={workspaceId}
        />
      )}

      {userToModify && modifyUserSidebar.isOpen && (
        <ModifyUserSidebar
          isOpen={modifyUserSidebar.isOpen}
          onClose={modifyUserSidebar.close}
          user={userToModify}
          workspaceId={workspaceId}
        />
      )}

      {children}
    </UsersActionsContext.Provider>
  );
};

export const useUsersActions = () => {
  const context = useContext(UsersActionsContext);
  if (!context) {
    throw new Error('useUsersActions must be used within a UsersActionsProvider');
  }
  return context;
};
